import { z } from '../zod';

const PAGE_STATUS_ENUM = Object.freeze({
  PUBLISHED: 'published',
  DRAFT: 'draft',
  ARCHIVED: 'archived',
});

export const PageStatusEnumValidationSchema = z.enum(Object.values(PAGE_STATUS_ENUM)).openapi({
  description: 'Publication status of the page.',
  example: PAGE_STATUS_ENUM.PUBLISHED,
});

const locales = [
  { code: 'ar-SA', country: 'Saudi Arabia' },
  { code: 'bn-BD', country: 'Bangladesh' },
  { code: 'bn-IN', country: 'India' },
  { code: 'cs-CZ', country: 'Czech Republic' },
  { code: 'da-DK', country: 'Denmark' },
  { code: 'de-AT', country: 'Austria' },
  { code: 'de-CH', country: 'Switzerland' },
  { code: 'de-DE', country: 'Germany' },
  { code: 'el-GR', country: 'Greece' },
  { code: 'en-AU', country: 'Australia' },
  { code: 'en-CA', country: 'Canada' },
  { code: 'en-GB', country: 'United Kingdom' },
  { code: 'en-IE', country: 'Ireland' },
  { code: 'en-IN', country: 'India' },
  { code: 'en-NZ', country: 'New Zealand' },
  { code: 'en-US', country: 'United States' },
  { code: 'en-ZA', country: 'South Africa' },
  { code: 'es-AR', country: 'Argentina' },
  { code: 'es-CL', country: 'Chile' },
  { code: 'es-CO', country: 'Columbia' },
  { code: 'es-ES', country: 'Spain' },
  { code: 'es-MX', country: 'Mexico' },
  { code: 'es-US', country: 'United States' },
  { code: 'fi-FI', country: 'Finland' },
  { code: 'fr-BE', country: 'Belgium' },
  { code: 'fr-CA', country: 'Canada' },
  { code: 'fr-CH', country: 'Switzerland' },
  { code: 'fr-FR', country: 'France' },
  { code: 'he-IL', country: 'Israel' },
  { code: 'hi-IN', country: 'India' },
  { code: 'hu-HU', country: 'Hungary' },
  { code: 'id-ID', country: 'Indonesia' },
  { code: 'it-CH', country: 'Switzerland' },
  { code: 'it-IT', country: 'Italy' },
  { code: 'jp-JP', country: 'Japan' },
  { code: 'ko-KR', country: 'Republic of Korea' },
  { code: 'nl-BE', country: 'Belgium' },
  { code: 'nl-NL', country: 'The Netherlands' },
  { code: 'no-NO', country: 'Norway' },
  { code: 'pl-PL', country: 'Poland' },
  { code: 'pt-BR', country: 'Brazil' },
  { code: 'pt-PT', country: 'Portugal' },
  { code: 'ro-RO', country: 'Romania' },
  { code: 'ru-RU', country: 'Russian Federation' },
  { code: 'sk-SK', country: 'Slovakia' },
  { code: 'sv-SE', country: 'Sweden' },
  { code: 'ta-IN', country: 'India' },
  { code: 'ta-LK', country: 'Sri Lanka' },
  { code: 'th-TH', country: 'Thailand' },
  { code: 'tr-TR', country: 'Turkey' },
  { code: 'zh-CN', country: 'China' },
  { code: 'zh-HK', country: 'Hond Kong' },
  { code: 'zh-TW', country: 'Taiwan' },
];

const LOCALE_CODES = locales.map(locale => locale.code);
export const LocaleCodeEnumValidationSchema = z.enum(LOCALE_CODES).openapi({
  description:
    'Locale used to format prices and numbers on the page, for example `en-US` shows $1,234.56 and `de-DE` shows 1.234,56 $. Defaults to `en-US`.',
  example: 'en-US',
});

const HOSTED_LAYOUT_TYPE_DESCRIPTION =
  'Layout of the hosted page. `center` shows the content and the checkout form in one centered card on a page background, `double` shows them side by side in two columns, and `split` divides the whole page into two halves, the content on one and the checkout form on the other.';

// double_banner is a legacy layout: pages may still have it, but the dashboard no longer offers it.
export const HostedLayoutTypeInputValidationSchema = z.enum(['center', 'double', 'split']).meta({
  description: `${HOSTED_LAYOUT_TYPE_DESCRIPTION} \`null\` resets it to the default, \`double\`.`,
  example: 'center',
});

export const HostedLayoutTypeResponseValidationSchema = z
  .enum(['center', 'double', 'split', 'double_banner'])
  .meta({
    description: `${HOSTED_LAYOUT_TYPE_DESCRIPTION} \`double_banner\` is a legacy layout that can't be set through the API.`,
    example: 'double',
  });

export const PAGE_TYPE_ENUM = Object.freeze({
  CHECKOUT: 'checkout', // results in a purchase
  EVENT: 'event', // results in an order with tickets
  FORM: 'form', // results in a submission
});

export const PageTypeEnumValidationSchema = z.enum(Object.values(PAGE_TYPE_ENUM)).openapi({
  description: 'Type of the page.',
  example: PAGE_TYPE_ENUM.CHECKOUT,
});

export const STRIPE_PAYMENT_METHODS = {
  ACSS_DEBIT: 'acss_debit',
  AFFIRM: 'affirm',
  AFTERPAY_CLEARPAY: 'afterpay_clearpay',
  ALIPAY: 'alipay',
  AMAZON_PAY: 'amazon_pay',
  AU_BECS_DEBIT: 'au_becs_debit',
  BACS_DEBIT: 'bacs_debit',
  BANCONTACT: 'bancontact',
  BLIK: 'blik',
  BOLETO: 'boleto',
  CARD: 'card',
  CARD_PRESENT: 'card_present',
  CASHAPP: 'cashapp',
  CUSTOMER_BALANCE: 'customer_balance',
  EPS: 'eps',
  FPX: 'fpx',
  GIROPAY: 'giropay',
  GRABPAY: 'grabpay',
  IDEAL: 'ideal',
  INTERAC_PRESENT: 'interac_present',
  KLARNA: 'klarna',
  KONBINI: 'konbini',
  LINK: 'link',
  MOBILEPAY: 'mobilepay',
  MULTIBANCO: 'multibanco',
  OXXO: 'oxxo',
  P24: 'p24',
  PAYNOW: 'paynow',
  PAYPAL: 'paypal',
  PIX: 'pix',
  PROMPTPAY: 'promptpay',
  REVOLUT_PAY: 'revolut_pay',
  SEPA_DEBIT: 'sepa_debit',
  SOFORT: 'sofort',
  SWISH: 'swish',
  TWINT: 'twint',
  US_BANK_ACCOUNT: 'us_bank_account',
  WECHAT_PAY: 'wechat_pay',
  ZIP: 'zip',
};

export const StripePaymentMethodEnumValidationSchema = z
  .enum(Object.values(STRIPE_PAYMENT_METHODS))
  .openapi({
    description: 'Stripe payment method type.',
    example: STRIPE_PAYMENT_METHODS.CARD,
  });
